/**
 * The seal, opened. This module is the premium reader's crypto path.
 * It is fully built and fully dormant: SEAL_LAUNCH is false and no
 * runtime path passes key material. If the right 32 bytes arrive, the
 * check passes, the dispatch opens, and the desk prints your name
 * somewhere between the corrections and the weather.
 *
 * Chain (see notes doc §2–§5): legs (ascii, A then B then C) ->
 * SHA-256 -> 32-byte key material -> HMAC-SHA256(key, standing motto)
 * -> 32-byte digest -> AES-256-GCM key for the sealed dispatch.
 * The motto is editorial rule 9 and does not live in this artifact.
 */

import { checkSealDigest, SEAL_LAUNCH } from "./entitlements-v3";
import { renderPremium } from "./sealed-reader";
import { SEALED_DISPATCH_IV_B64, SEALED_DISPATCH_CT_B64 } from "./sealed-payload";

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c?.subtle) throw new Error("seal: platform crypto unavailable");
  return c.subtle;
}

/** SHA-256 of the assembled legs (plain ascii, no separators). */
export async function keyMaterial(legsConcat: string): Promise<Uint8Array> {
  const buf = await subtle().digest("SHA-256", new TextEncoder().encode(legsConcat));
  return new Uint8Array(buf);
}

/** HMAC-SHA256(keyMaterial, message) — the seal digest. */
export async function sealDigest(keyBytes: Uint8Array, message: string): Promise<{ bytes: Uint8Array; hex: string }> {
  const key = await subtle().importKey("raw", keyBytes as unknown as ArrayBuffer, { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  const sig = await subtle().sign("HMAC", key, new TextEncoder().encode(message));
  const bytes = new Uint8Array(sig);
  return { bytes, hex: bytesToHex(bytes) };
}

/** Verify a digest hex against the published expectation. */
export function verify(hex: string): boolean {
  return checkSealDigest(hex).ok;
}

/** AES-256-GCM decrypt of the sealed dispatch with the seal digest. */
export async function unseal(digestBytes: Uint8Array): Promise<string> {
  const key = await subtle().importKey("raw", digestBytes as unknown as ArrayBuffer, { name: "AES-GCM" }, false, ["decrypt"]);
  const plain = await subtle().decrypt(
    { name: "AES-GCM", iv: b64ToBytes(SEALED_DISPATCH_IV_B64) as unknown as ArrayBuffer },
    key,
    b64ToBytes(SEALED_DISPATCH_CT_B64) as unknown as ArrayBuffer,
  );
  return new TextDecoder().decode(plain);
}

/**
 * The full chain, the way QA runs it: assemble the legs, hash, HMAC
 * with the standing motto, verify, decrypt. Renders the premium
 * reader only when the launch flag is on; otherwise returns the text
 * to the caller. Production callers pass nothing. Production callers
 * are readers.
 */
export async function openSeal(legsConcat: string, motto: string): Promise<string> {
  const keyBytes = await keyMaterial(legsConcat);
  const { bytes, hex } = await sealDigest(keyBytes, motto);
  const verdict = checkSealDigest(hex);
  if (!verdict.ok) throw new Error(`seal: digest rejected (${verdict.reason})`);
  const text = await unseal(bytes);
  if (SEAL_LAUNCH) {
    renderPremium(text);
  }
  return text;
}

/** Low-level door for the QA harness: skip derivation, bring a digest. */
export async function tryOpenSealed(digestHex: string): Promise<string> {
  if (!verify(digestHex)) throw new Error("seal: digest rejected (prefix-mismatch)");
  return unseal(hexToBytes(digestHex));
}
